import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetMeterConsumptionQuery, useGetLastPurchaseQuery } from "../../../../../service/vending-service";
import styles from "./PrepaidEnergyProfileBox.module.scss";
import { MainValue } from "../../../../../common/components/main-value/MainValue";

const LightningBolt = () => (
	<svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M9 1L1 13H8L7 21L15 9H8L9 1Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
	</svg>
);

const CircularTracker: React.FC<{ value?: number; max?: number }> = ({ value, max }) => {
	const radius = 20;
	const circumference = 2 * Math.PI * radius;
	const fill = value != null && max != null && max > 0 ? circumference - (value / max) * circumference : circumference;

	return (
		<svg width="48" height="48" viewBox="0 0 48 48" className={styles.tracker}>
			<circle cx="24" cy="24" r={radius} strokeWidth="4" className={styles.trackerBg} />
			{value != null && (
				<circle
					cx="24"
					cy="24"
					r={radius}
					strokeWidth="4"
					className={styles.trackerFill}
					strokeDasharray={circumference}
					strokeDashoffset={fill}
					strokeLinecap="round"
					transform="rotate(-90 24 24)"
				/>
			)}
			<foreignObject x="12" y="13" width="24" height="24">
				<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
					<LightningBolt />
				</div>
			</foreignObject>
		</svg>
	);
};

const NoDataBadge: React.FC<{ light?: boolean }> = ({ light }) => {
	const { t } = useTranslation();
	return <span className={light ? styles.noDataLight : styles.noData}>{t("common.labels.noData")}</span>;
};

const toLocalDateTimeString = (date: Date): string => {
	const p = (n: number) => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
};

export interface PrepaidEnergyProfileCardsProps {
	lastPurchaseUnits: string | null;
	consumptionKwh: string | null;
	unitRemaining: number | null;
	maxUnits?: number;
	lastPurchaseLoading?: boolean;
	consumptionLoading?: boolean;
	onArrowClick?: () => void;
}

/**
 * Presentational half of the box — no data fetching, so mockups can render it with
 * dummy values (see UtilityPhonePreview in the BPS admin utility setup).
 */
export const PrepaidEnergyProfileCards: React.FC<PrepaidEnergyProfileCardsProps> = ({
	lastPurchaseUnits,
	consumptionKwh,
	unitRemaining,
	maxUnits,
	lastPurchaseLoading,
	consumptionLoading,
	onArrowClick,
}) => {
	const { t } = useTranslation();

	return (
		<div className={styles.container}>
			{/* Left card: white */}
			<div className={styles.leftCard}>
				<div className={styles.lastPurchase}>
					<span className={styles.label}>{t("energyManagement.lastPurchase")}</span>
					{lastPurchaseLoading ? (
						<span className={styles.shimmer} />
					) : lastPurchaseUnits ? (
						<MainValue value={lastPurchaseUnits} unit={t("energyManagement.units")} />
					) : (
						<NoDataBadge />
					)}
				</div>

				<div className={styles.consumptionSection}>
					<div className={styles.consumptionLabel}>
						<span className={styles.labelSm}>{t("common.labels.todays")}</span>
						<span className={styles.labelMd}>{t("energyManagement.consumption")}</span>
					</div>
					<div className={styles.consumptionRow}>
						{consumptionLoading ? (
							<span className={styles.shimmer} />
						) : consumptionKwh ? (
							<span className={styles.consumptionValue}>{consumptionKwh}</span>
						) : (
							<NoDataBadge />
						)}
						<button className={styles.arrowBtn} onClick={onArrowClick} aria-label={t("home.web.goToEnergyManagement")}>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</div>
				</div>
			</div>

			{/* Right card: dark */}
			<div className={styles.rightCard}>
				<div className={styles.unitTop}>
					<span className={styles.labelLight}>{t("energyManagement.unitRemaining")}</span>
					{lastPurchaseLoading ? (
						<span className={styles.shimmerLight} />
					) : unitRemaining != null ? (
						<span className={styles.unitRemainingValue}>{unitRemaining.toFixed(2)}</span>
					) : (
						<NoDataBadge light />
					)}
				</div>
				<CircularTracker value={unitRemaining ?? undefined} max={maxUnits} />
			</div>
		</div>
	);
};

export const PrepaidEnergyProfileBox: React.FC = () => {
	const navigate = useNavigate();

	const todayParams = useMemo(() => {
		const now = new Date();
		const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		return { from: toLocalDateTimeString(start), to: toLocalDateTimeString(now), isMonthly: false };
	}, []);

	const { data: consumptionData, isLoading: consumptionLoading } = useGetMeterConsumptionQuery(todayParams);
	const { data: lastPurchaseData, isLoading: lastPurchaseLoading } = useGetLastPurchaseQuery();

	const consumptionKwh = consumptionData?.consumption != null ? `${consumptionData.consumption.toFixed(2)}kWh` : null;
	const lastPurchaseUnits = lastPurchaseData?.lastPurchase != null ? `${lastPurchaseData.lastPurchase}` : null;
	const unitRemaining = lastPurchaseData?.unitRemaining ?? null;

	return (
		<PrepaidEnergyProfileCards
			lastPurchaseUnits={lastPurchaseUnits}
			consumptionKwh={consumptionKwh}
			unitRemaining={unitRemaining}
			maxUnits={lastPurchaseData?.lastPurchase ?? undefined}
			lastPurchaseLoading={lastPurchaseLoading}
			consumptionLoading={consumptionLoading}
			onArrowClick={() => navigate("/energy-management")}
		/>
	);
};
